class SmartTables {
    /*
    In order for making the tables smart:
    - the table must have more than config.rowLimit lines available
    - the tbody cells should not span through columns or rows
    - there should be thead th for the event listeners
     */

    /**
     * @param {object} config - The configuration object.
     * @param {object} config.classes - the classes used mainly CSS styling
     * @param {string} config.classes.filter_table - the class of the table that has filtering enabled
     * @param {string} config.classes.filter_tr - the class of the tr that has the filtering input fields
     * @param {string} config.classes.filter_active - the class we use in order to visually hide the filtered rows
     * @param {string} config.classes.even - the class we use in order to visually enhance filtered even rows
     * @param {string} config.classes.odd - the class we use in order to visually enhance filtered odd rows
     * @param {string} config.classes.sort_table - the class of the table that has sorting enabled
     * @param {string} config.enabled - if the functionality is enabled and need to be initiated
     * @param {[string]} config.enableFor - for what article type we should enable this (it's crucial to control the table data)
     * @param {boolean} config.filter - if the filtering function is enabled
     * @param {boolean} config.rowLimit - the number of rows the table must have in order to be enhanced
     * @param {string} config.sort - if the sorting function is enabled
     * @param {[object]} tables - The configuration object.
     * @param {HTMLElement} tables.ref - the table data from the preprocessor layer.
     * @param {boolean} tables.smartCandidate - if the table is a candidate for becoming "smart".
     * @param {HTMLElement} [ref] - provide a reference element to manipulate the tables inside,
     *                              if none is provided the {tables.ref} will be used instead.
     */
    constructor(config, tables, ref = undefined) {
        this.config = config;
        this.classes = config.classes;
        this.ref = ref;

        if (this.config.enabled) {
            // filter table object to only contain the candidates
            this.tables = tables?.filter(table => table.smartCandidate);

            if (this.tables.length === 0) return;

            if (document.body.querySelectorAll(this.config.enableFor).length > 0) {
                this.init();
            }
        }
    }

    init() {
        try {
            this.makeSmartTable(this.tables, this.config.sort, this.config.filter);
        } catch (error) {
            console.error('Make smart tables:', error);
        }
    }

    makeSmartTable(tables, enableSort, enableFilter) {
        tables.forEach(obj => {
            const table = (this.ref?.querySelector('#' + obj.id) || obj.ref).querySelector('table');
            if (!table) return;

            const filtered_headings = [...table.querySelectorAll('thead th')].filter(th => th.colSpan === 1);

            if (enableSort && !table.classList.contains(this.classes.sort_table)) {
                this._sort_table(table, filtered_headings);

                const tHeadRows = [...table.tHead.rows].filter(row => !row.classList.contains(this.classes.filter_tr));
                tHeadRows.forEach((row, rowIndex) => {
                    const rowTh = row.querySelectorAll('th');
                    let indexValue = 0;
                    rowTh.forEach((th, thIndex) => {
                        if (th.colSpan === 1) {
                            const rowSpan = tHeadRows[rowIndex - 1]?.querySelectorAll('th')[thIndex]?.rowSpan;
                            if (rowSpan > 1) {
                                indexValue += rowSpan;
                            }
                            this._sort_th(table, th, indexValue);
                        }
                        indexValue += th.colSpan;
                    });
                });
            }

            if (enableFilter && !table.classList.contains(this.classes.filter_table)) {
                const bodyTDNumber =
                    table.tBodies[0]?.rows[0]?.getElementsByTagName('TD').length || filtered_headings.length;

                this._filter_table(table);
                this._filter_thead(table, bodyTDNumber);
            }
        });
    }

    _check_zebra_coloring(table) {
        for (let tbody of table.tBodies) {
            const rows = tbody.rows;
            if (tbody.querySelector('.' + this.classes.filter_active)) {
                tbody.dataset.shading = 'custom';
                let order = 'odd';
                for (let i = 0; i < rows.length; i++) {
                    const isFiltered = rows[i].classList.contains(this.classes.filter_active);
                    rows[i].classList.remove(this.classes.even, this.classes.odd);
                    if (!isFiltered) {
                        rows[i].classList.add(this.classes[order]);
                        order = order === 'odd' ? 'even' : 'odd';
                    }
                }
            } else {
                tbody.removeAttribute('data-shading');
                for (let i = 0; i < rows.length; i++) {
                    rows[i].classList.remove(this.classes.even, this.classes.odd);
                }
            }
        }
    }

    _filter(colIndex, table, input) {
        if (input.value === '' || input.value.length < input.dataset.value.length) {
            table.dispatchEvent(new Event('updateFiltering'));

            const inputs = table.querySelectorAll('thead input');
            [...inputs].forEach((inp, index) => this._filter_fn(index, table, inp));
        } else {
            this._filter_fn(colIndex, table, input);
        }
        this._check_zebra_coloring(table);
    }
    _filter_fn(index, table, input) {
        const value = input.value;
        for (let tbody of table.tBodies) {
            const rows = tbody.rows;
            for (let i = 0; i < rows.length; i++) {
                const col = rows[i].getElementsByTagName('TD')[index];
                if (!col.innerText.toLowerCase().includes(value.toLowerCase())) {
                    rows[i].classList.add(this.classes.filter_active);
                }
            }
        }
        input.dataset.value = value;
    }
    _filter_table(table) {
        table.addEventListener('updateFiltering', () => {
            const trs = table.querySelectorAll('tbody tr');
            trs.forEach(tr => tr.classList.remove(this.classes.filter_active));
        });
        table.classList.add(this.classes.filter_table);
    }
    _filter_thead(table, colNumber) {
        const thead = table.querySelector('thead');
        const tr = document.createElement('tr');
        tr.classList.add(this.classes.filter_tr);
        for (let i = 0; i < colNumber; i++) {
            const th = document.createElement('th');
            const input = document.createElement('input');
            input.dataset.value = '';
            input.addEventListener('input', e => this._filter(i, table, e.target));
            input.setAttribute('placeholder', 'Type to search');
            th.append(input);
            tr.append(th);
        }
        thead.append(tr);
    }

    _sort_fn(index, table, dir) {
        const isNumeric = n => {
            const pattern = /^[0-9,]*$/g;
            return pattern.test(n) || isFinite(n);
            // return (Object.prototype.toString.call(n) === '[object Number]' || Object.prototype.toString.call(n) === '[object String]')
            //     && !isNaN(parseFloat(n))
            //     && isFinite(n.toString().replace(/^-/, ''));
        };

        let i, shouldSwitch;
        let switching = true;
        // Make a loop that will continue until no switching has been done:
        for (let tbody of table.tBodies) {
            const clone = tbody.cloneNode(true);
            const rows = clone.rows;
            while (switching) {
                // Start by saying: no switching is done and loop through all table rows
                switching = false;
                for (i = 0; i < rows.length - 1; i++) {
                    // Start by saying there should be no switching:
                    shouldSwitch = false;
                    // Get the two elements you want to compare, one from current row and one from the next:
                    const x = rows[i].getElementsByTagName('TD')[index];
                    const y = rows[i + 1].getElementsByTagName('TD')[index];

                    let xValue = x.innerText.toLowerCase();
                    let yValue = y.innerText.toLowerCase();
                    if (isNumeric(x.innerText) && isNumeric(y.innerText)) {
                        xValue = Number(xValue.replace(',', ''));
                        yValue = Number(yValue.replace(',', ''));
                    }

                    // Check if the two rows should switch place, based on the direction, asc or desc:
                    if (dir === 'asc') {
                        if (xValue > yValue) {
                            // If so, mark as a switch and break the loop:
                            shouldSwitch = true;
                            break;
                        }
                    } else if (dir === 'desc') {
                        if (xValue < yValue) {
                            // If so, mark as a switch and break the loop:
                            shouldSwitch = true;
                            break;
                        }
                    }
                }
                if (shouldSwitch) {
                    // If a switch has been marked, make the switch and mark that a switch has been done:
                    rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
                    switching = true;
                }
            }
            tbody.innerHTML = clone.innerHTML;
        }
        this._check_zebra_coloring(table);
    }
    _sort_table(table, headings) {
        table.addEventListener('updateSorting', e => {
            const filtered = [...headings].filter(heading => heading !== e.detail);
            filtered.forEach(th => (th.dataset.direction = 'none'));
        });
        table.classList.add(this.classes.sort_table);
    }
    _sort_th(table, th, value) {
        th.dataset.direction = 'none';
        th.setAttribute('tabIndex', 0);
        th.addEventListener('click', () => {
            table.dispatchEvent(new CustomEvent('updateSorting', {detail: th}));
            const dataDirection = th.dataset.direction;
            const newDirection = dataDirection === 'asc' ? 'desc' : 'asc';
            th.dataset.direction = newDirection;
            this._sort_fn(value, table, newDirection);
        });
    }
}

module.exports = SmartTables;
